import React, { useCallback } from 'react'
import PropTypes from 'prop-types'

import { useSetPromoCode } from './PromoCodeMutation'

import { Field } from 'formik'
import { toast } from 'react-toastify'

import { ToastNotification, toastEachWarning } from '../Modifiers/ToastNotification/ToastNotification'
import Error from '../Error/Error'
import Input from '../Form/Input/Input'
import { PromoCode } from './PromoCode/PromoCode'
import { PromoCodeLabel } from './PromoCodeLabel/PromoCodeLabel'

import './PromoCodeContainer.css'
import { useFormik } from '../FormikProvider/FormikProvider'

const PromoCodeInputId = 'PromoCodeInput'

export const PromoCodeContainer = ({ cart }) => {
  const {
    values,
    touched,
    setFieldValue,
    setFieldError,
    setFieldTouched
  } = useFormik()

  const [setPromoCode, { error, loading: setPromoCodeLoading, refetch }] = useSetPromoCode({
    onComplete: () => {
      if (values.promoCode) {
        setFieldValue('promoCode', '')
      }

      // show toast message for adding promo code
      toast(<ToastNotification checkmark>Promo code has been applied!</ToastNotification>)
    },
    onError: () => {
      // show field as invalid if cart modification error or promo code application error is returned
      document.getElementById('PromoCodeInput').focus()
      setFieldError('promoCode', 'This promo code is invalid.')
    },
    onCartOutOfStock: (message) => {
      // in the rare case that an item in cart went out of stock while user is applying promo code,
      // show out of stock warning
      toastEachWarning({ warnings: [message] })
    }
  })

  const handleApplyPromoCode = useCallback(() => {
    setPromoCode({
      variables: {
        input: {
          cartGuid: cart.guid,
          promoCode: values.promoCode,
          discountType: 'RESTAURANT'
        }
      }
    })
  }, [ setPromoCode, cart.guid, values.promoCode ])

  // submit promo code on enter press
  const handleKeyPress = (evt) => {
    if (evt.key === 'Enter') {
      evt.preventDefault()

      // set field touched manually, default happens on blur
      if (!touched['promoCode']) {
        setFieldTouched('promoCode', true)
      }

      handleApplyPromoCode()
    }
  }

  /**
   * form.touched.fieldName is not set until input blurs, need to check for field
   * value to enable/disable apply button
   * */
  const isDirty = values.promoCode.trim() !== ''

  const { order: { discounts = {} } } = cart
  const { restaurantDiscount } = discounts

  return (
    <div styleName='PromoCodeContainer'>
      {
        restaurantDiscount ? (
          <PromoCode name={restaurantDiscount.promoCode} cartGuid={cart.guid} />
        ) : (
          <React.Fragment>
            { Boolean(error) && <Error error={error} retry={refetch} />}
            <Field
              id={PromoCodeInputId}
              component={Input}
              type='text'
              name='promoCode'
              label='Promo Code'
              endAdornment={
                <PromoCodeLabel
                  canSubmit={isDirty}
                  applyPromoCode={handleApplyPromoCode}
                  loading={setPromoCodeLoading}
                  noAdornmentMargin
                />
              }
              onKeyDown={handleKeyPress}
            />
          </React.Fragment>
        )
      }
    </div>
  )
}

PromoCodeContainer.propTypes = {
  cart: PropTypes.object.isRequired
}
